import { CARD_EXCHANGE_OFFER_TTL_SECONDS, CARD_SHARE_QR_MAX_LENGTH, CARD_SHARE_QR_ORIGIN_SHAPE, CARD_SHARE_QR_PATH, CARD_SHARE_TOKEN_TTL_SECONDS, EXCHANGE_PREVIEW_FIELDS, } from './card-exchange';
/**
 * AKIŞ C SÖZLEŞME DEĞİŞMEZLERİ — TEST İKİZİ ÜRETİMDEN SAPAMASIN DİYE.
 *
 * NEDEN VAR (QA Blok 22 P2-1): mobil test ikizi (`fake-exchange-server.ts`)
 * üretimden sessizce ayrışmıştı — paylaşım önizlemesinin adını SEÇİLEN KARTTAN
 * veriyordu, üretim ise hesabın `display_name`'inden. Ekranda aynı anda iki
 * farklı ad görünüyordu ama mobil paket 85/85 yeşildi: ikiz hatayı MASKELEDİ.
 * Aynı sınıfın diğer örnekleri: ikizin jeton `expiresAt`i sabit bir 2026
 * tarihiydi (üretimde TTL kadar ileri), teklif `createdAt`i sabitti.
 *
 * KONTROLÜN ŞEKLİ (CLAUDE.md 1a): sınıf CÜMLEYLE değil, İKİ TARAFTA DA KOŞAN
 * TEK LİSTEYLE kapatılır. Aşağıdaki değişmezler saf fonksiyonlardır ve DTO'nun
 * ANLAMINI sorar (şeklini TypeScript zaten kilitliyor):
 *
 *   - mobil `test-double-contract.spec.ts` bunları TEST İKİZİNİN yanıtlarına,
 *   - API `card-exchange-contract.e2e-spec.ts` aynı listeyi GERÇEK UÇLARIN
 *     yanıtlarına uygular.
 *
 * Yani ikiz üretimden saparsa mobil taraf, üretim sözleşmeden saparsa API
 * tarafı KIRILIR. Listeye yeni bir değişmez eklendiğinde iki spec de onu
 * otomatik koşar; bir spec listeyi eksik kapsarsa "hepsi enumere edildi"
 * asertiyle kırılır.
 */
/** Saat kayması payı: iki taraf da `now`u kendi ölçtüğü an üzerinden verir. */
const CLOCK_SKEW_SECONDS = 30;
function selectedCardOf(input) {
    return input.myCards.find((card) => card.id === input.share.card.cardId);
}
export const SHARE_INVARIANTS = [
    {
        /** Önizleme BENİM kartımdır: paylaşılan kart kimliği kart listemde olmalı. */
        id: 'share.cardIsMine',
        check({ share, myCards }) {
            const known = myCards.some((card) => card.id === share.card.cardId);
            return known ? null : `paylaşılan kart (${share.card.cardId}) kart listemde yok`;
        },
    },
    {
        /**
         * ADIN KAYNAĞI KARTTIR, HESAP DEĞİL. Çoklu kart ürünün temel özelliğidir;
         * takma adlı/ikinci kartını paylaşan kullanıcı o kartın adıyla görünür.
         */
        id: 'share.nameComesFromSelectedCard',
        check(input) {
            const card = selectedCardOf(input);
            if (!card)
                return null; // `share.cardIsMine` zaten kırılır.
            return input.share.card.displayName === card.fullName
                ? null
                : `önizleme adı "${String(input.share.card.displayName)}" ≠ kartın adı "${card.fullName}"`;
        },
    },
    {
        /** Unvan ve şirket de aynı karttan gelir (ad ile ayrışamaz). */
        id: 'share.roleComesFromSelectedCard',
        check(input) {
            const card = selectedCardOf(input);
            if (!card)
                return null;
            const sameTitle = input.share.card.jobTitle === card.jobTitle;
            const sameCompany = input.share.card.companyName === card.companyName;
            return sameTitle && sameCompany
                ? null
                : `önizlemenin unvan/şirketi seçilen kartla aynı değil (${String(input.share.card.jobTitle)} · ${String(input.share.card.companyName)})`;
        },
    },
    {
        /**
         * JETON KISA ÖMÜRLÜDÜR. İkizin sabit tarihi bu değişmezi düşürüyordu ve
         * "ekranda ölü kod duruyor" kusuru (QA P2-2) hiç ölçülemiyordu.
         */
        id: 'share.tokenTtlMatchesContract',
        check({ share, now }) {
            const left = (Date.parse(share.expiresAt) - now) / 1000;
            if (Number.isNaN(left))
                return `expiresAt okunamadı: ${share.expiresAt}`;
            if (left <= 0)
                return `jeton üretildiği anda ölü (kalan ${Math.round(left)} sn)`;
            return left <= CARD_SHARE_TOKEN_TTL_SECONDS + CLOCK_SKEW_SECONDS
                ? null
                : `jeton ömrü sözleşmeden uzun: ${Math.round(left)} sn > ${CARD_SHARE_TOKEN_TTL_SECONDS} sn`;
        },
    },
    {
        /**
         * KAREKOD YALNIZ KISA ÖMÜRLÜ JETONU TAŞIR (karekod kapsamı 02.11.2026).
         * Ya jetonun kendisidir ya da `<köken>/kart/<jeton>`; araya kalıcı kart ya
         * da kullanıcı kimliği giremez, adres yoğun karekod üretecek kadar uzayamaz.
         */
        id: 'share.qrValueCarriesOnlyToken',
        check({ share }) {
            if (share.qrValue === share.token)
                return null;
            const suffix = `/${CARD_SHARE_QR_PATH}/${share.token}`;
            const origin = share.qrValue.endsWith(suffix) ? share.qrValue.slice(0, -suffix.length) : null;
            if (origin === null || !CARD_SHARE_QR_ORIGIN_SHAPE.test(origin)) {
                return `karekod değeri jetondan ya da <köken>/${CARD_SHARE_QR_PATH}/<jeton> biçiminden farklı`;
            }
            return share.qrValue.length <= CARD_SHARE_QR_MAX_LENGTH
                ? null
                : `karekod değeri ${share.qrValue.length} karakter > ${CARD_SHARE_QR_MAX_LENGTH}`;
        },
    },
];
export const OFFER_INVARIANTS = [
    {
        /** D2 — onay öncesi alan kümesi KİLİTLİ: fazla ya da eksik alan olamaz. */
        id: 'offer.previewFieldSetIsLocked',
        check({ offer }) {
            const actual = Object.keys(offer.peer).sort();
            const expected = [...EXCHANGE_PREVIEW_FIELDS].sort();
            return actual.join(',') === expected.join(',')
                ? null
                : `önizleme alan kümesi kilitten farklı: [${actual.join(', ')}]`;
        },
    },
    {
        /** KIRMIZI ÇİZGİ 1: kayıt yalnız İKİ onayla doğar. */
        id: 'offer.encounterRequiresBothApprovals',
        check({ offer }) {
            if (offer.encounterId === null)
                return null;
            return offer.approvals.mine && offer.approvals.peer
                ? null
                : 'encounterId dolu ama iki onay damgası tam değil';
        },
    },
    {
        /** Durum ve kayıt AYRIŞAMAZ: `confirmed` ⇔ `encounterId` dolu. */
        id: 'offer.confirmedIffEncounter',
        check({ offer }) {
            const confirmed = offer.status === 'confirmed';
            return confirmed === (offer.encounterId !== null)
                ? null
                : `status="${offer.status}" ile encounterId=${String(offer.encounterId)} çelişiyor`;
        },
    },
    {
        /** Teklifin ömrü sözleşmedeki TTL'dir ve AÇILDIĞI ANDAN sayılır (D3). */
        id: 'offer.ttlMatchesContract',
        check({ offer }) {
            const life = (Date.parse(offer.expiresAt) - Date.parse(offer.createdAt)) / 1000;
            if (Number.isNaN(life))
                return `expiresAt/createdAt okunamadı`;
            return Math.abs(life - CARD_EXCHANGE_OFFER_TTL_SECONDS) <= CLOCK_SKEW_SECONDS
                ? null
                : `teklif ömrü ${Math.round(life)} sn, sözleşme ${CARD_EXCHANGE_OFFER_TTL_SECONDS} sn`;
        },
    },
    {
        /** Teklif AÇILDIĞINDA yaşıyor olmalı: ölü doğan teklif akışı kilitler. */
        id: 'offer.isAliveWhenOpened',
        check({ offer, now }) {
            if (offer.status !== 'pending')
                return null;
            return Date.parse(offer.expiresAt) > now
                ? null
                : `pending teklifin süresi zaten dolmuş (${offer.expiresAt})`;
        },
    },
];
/** İki tarafın da "hepsini koştum" diyebilmesi için tek kimlik listesi. */
export const CARD_EXCHANGE_INVARIANT_IDS = [
    ...SHARE_INVARIANTS.map((invariant) => invariant.id),
    ...OFFER_INVARIANTS.map((invariant) => invariant.id),
];
/** Sapmaları toplar — çağıran hepsini tek seferde raporlayabilsin. */
export function collectShareViolations(input) {
    return SHARE_INVARIANTS.map((invariant) => {
        const result = invariant.check(input);
        return result === null ? null : `${invariant.id}: ${result}`;
    }).filter((message) => message !== null);
}
export function collectOfferViolations(input) {
    return OFFER_INVARIANTS.map((invariant) => {
        const result = invariant.check(input);
        return result === null ? null : `${invariant.id}: ${result}`;
    }).filter((message) => message !== null);
}
//# sourceMappingURL=card-exchange-invariants.js.map